/**
 * Category tile resolver (T-201 / T-202) — frontend display fallback.
 *
 * Guarantees no POS product card is ever image-free, even for tenants whose
 * categories were never seeded/backfilled with a `Category.imageUrl` (custom
 * category names, pre-migration tenants). Maps a category name **or** slug to
 * one of the bundled vector tiles in `public/category-images/<group>.svg`.
 *
 * Precedence in the card is: product.imageUrl → category.imageUrl → this
 * resolver (→ `default.svg` for unknown names) → lucide `Package` icon on load error.
 * All vector, never emoji. Mirrors the backend `categoryTileUrl` (seed-templates.ts)
 * but with richer keyword matching so real-world custom names resolve too.
 */

const TILE_GROUPS = [
  'agr', 'automotive', 'baby', 'bakery', 'construction', 'default', 'elec',
  'food', 'grocery', 'home', 'household-cleaning', 'ind', 'jwl',
  'kitchen-cookware', 'ofc', 'personal-care', 'pet', 'phm', 'rest', 'spt',
  'tex', 'tobacco',
] as const;

export type TileGroup = (typeof TILE_GROUPS)[number];

/** Ordered keyword rules — first match wins. Keep specific groups before `grocery`. */
const TILE_RULES: ReadonlyArray<readonly [readonly string[], TileGroup]> = [
  [['food-beverage', 'food-and-bev'], 'food'],
  [['personal-care', 'pc-', 'cosmetic', 'beauty', 'makeup', 'skincare', 'deodorant', 'fragrance', 'perfume', 'hygiene', 'toiletr', 'shampoo', 'soap'], 'personal-care'],
  [['pharma', 'phm-', 'medicine', 'medical', 'health', 'pharmacy', 'vitamin', 'supplement'], 'phm'],
  [['bakery', 'baked', 'bread', 'cake', 'pastry', 'croissant', 'donut', 'rusk', 'biscuit', 'cookie'], 'bakery'],
  [['restaurant', 'rest-', 'main-course', 'mains', 'starter', 'dessert', 'biryani', 'pizza', 'burger', 'bbq', 'grill', 'soup', 'salad', 'chinese', 'continental', 'fried-chicken'], 'rest'],
  [['produce', 'vegetable', 'fruit', 'agri', 'agr-', 'seed', 'fertiliz', 'herbicide', 'pesticide', 'insecticide', 'crop', 'farm', 'livestock', 'poultry', 'irrigation'], 'agr'],
  [['electronic', 'elec-', 'mobile', 'phone', 'computer', 'laptop', 'tablet', 'camera', 'audio', 'television', 'appliance', 'printer', 'gaming', 'network'], 'elec'],
  [['cloth', 'apparel', 'textile', 'tex-', 'fsh-', 'fashion', 'garment', 'fabric', 'hosiery', 'undergarment', 'linen', 'shawl', 'bridal', 'shirt', 'shalwar', 'kameez', 'kurta', 'trouser', 'jeans', 'dupatta', 'saree', 'abaya', 'scarf'], 'tex'],
  [['jewel', 'jwl-', 'watch', 'gold', 'silver', 'gem', 'sunglass', 'optical'], 'jwl'],
  [['hardware', 'construction', 'con-', 'cement', 'steel', 'rebar', 'pipe', 'tile', 'plumb', 'sanitary', 'roofing', 'waterproof', 'fastener', 'adhesive', 'paint', 'coating'], 'construction'],
  [['industrial', 'ind-', 'welding', 'ppe', 'safety', 'power-tool', 'material-handling'], 'ind'],
  [['automotive', 'vehicle', 'motor', 'tyre', 'tire'], 'automotive'],
  [['sport', 'fitness', 'gym', 'cricket', 'football', 'tennis', 'badminton', 'cycling', 'swimming', 'martial'], 'spt'],
  [['pet-', 'pet-supplies', 'animal-health', 'aquarium'], 'pet'],
  [['baby', 'kids', 'infant', 'toy', 'child', 'diaper'], 'baby'],
  [['tobacco', 'cigarette', 'cigar'], 'tobacco'],
  [['clean', 'detergent', 'household', 'janitor', 'laundry'], 'household-cleaning'],
  [['office', 'ofc-', 'stationery', 'stationary', 'printing', 'packaging'], 'ofc'],
  [['kitchen', 'cookware', 'utensil', 'tableware', 'crockery', 'cutlery'], 'kitchen-cookware'],
  [['home', 'furniture', 'decor', 'bedding', 'lighting', 'garden', 'mattress'], 'home'],
  [['grocery', 'fmcg', 'beverage', 'drink', 'juice', 'soda', 'water', 'tea', 'coffee', 'snack', 'crisp', 'confection', 'chocolate', 'dairy', 'milk', 'cheese', 'yogurt', 'rice', 'flour', 'oil', 'ghee', 'sugar', 'salt', 'spice', 'masala', 'sauce', 'ketchup', 'pickle', 'pantry', 'staple', 'canned', 'frozen', 'noodle', 'pasta', 'pulse', 'lentil', 'raw-material', 'ingredient', 'meat', 'chicken', 'beef', 'mutton', 'seafood', 'fish', 'egg', 'cereal', 'jam', 'honey', 'food'], 'grocery'],
];

function slugify(input: string): string {
  return input.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-');
}

/** Resolve a category name or slug to a bundled tile group. */
export function resolveTileGroup(nameOrSlug: string | null | undefined): TileGroup {
  if (!nameOrSlug) return 'default';
  const s = slugify(nameOrSlug);
  for (const [keys, group] of TILE_RULES) {
    if (keys.some((k) => s.includes(k))) return group;
  }
  return 'default';
}

/** Public URL of the tile for a category name or slug (served from /public). */
export function categoryTileUrl(nameOrSlug: string | null | undefined): string {
  return `/category-images/${resolveTileGroup(nameOrSlug)}.svg`;
}

/** The generic fallback tile path (what an un-matched category is stored/seeded with). */
const DEFAULT_TILE_URL = '/category-images/default.svg';

/**
 * Single owner for "which image does this category show" (T-413). A stored
 * `Category.imageUrl` of the generic default tile must NOT suppress the keyword
 * resolver (the backend may have stored `default.svg` for a name the richer FE
 * rules can classify), so it is re-derived. Uploaded images and non-default
 * bundled tiles are returned unchanged.
 */
export function resolveCategoryTileSrc(
  imageUrl: string | null | undefined,
  nameOrSlug: string | null | undefined,
): string {
  if (!imageUrl || imageUrl === DEFAULT_TILE_URL) return categoryTileUrl(nameOrSlug);
  return imageUrl;
}
